<template>
  <div class="mx-auto">
    <div class="container px-8 report-print">
      <v-overlay
        :value="isLoading"
        class="text-center"
        color="grey darken-4"
        opacity="0.70"
      >
        <h4 class="mb-8">{{ $t(`insights.${downloadingText}`) }}</h4>
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <v-container fluid ref="reportPrint">
        <v-sheet
          v-if="sanitizedTitle"
          data-testid="report-print-header"
          color="white"
          class="report-print-header py-10 px-13"
        >
          <div class="d-flex justify-space-between">
            <p class="h4 my-0" v-html="sanitizedTitle"></p>
            <div class="d-flex flex-column align-end">
              <Logo class="float-right mb-2" />
              <p class="mb-0 body2">{{ dateRange }}</p>
            </div>
          </div>
        </v-sheet>
        <div v-for="(section, index) in sections" :key="section.title + index">
          <Section
            class="report-print-section py-0"
            :section="section"
            :elevation="0"
            data-private
            data-testid="report-print-section"
          />
        </div>
      </v-container>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import { utils } from 'frontend-js-shared'

import { createLetterPdf } from '@/lib/pdf'
import mixin from '@/mixins/mixin.vue'

import Logo from '@/components/OrganizationLogo.vue'
import Section from '@/containers/ReportsView/Section.vue'

const ACTIONABLE_WIDGETS = ['info-panel', 'action-list', 'empty-state']
const PRIVACY_LABEL = '(FOR INTERNAL USE ONLY)'

export default {
  name: 'InsightsPrint',

  mixins: [mixin],

  components: {
    Logo,
    Section,
  },

  data() {
    return {
      isLoading: true,
      hasBeenDownloaded: false,
      reportData: {},
      routeQuery: this.getRouteQuery(),
      notAllowedWidgetsTypes: ACTIONABLE_WIDGETS,
      downloadingText: 'generating_report',
    }
  },

  mounted() {
    this.getInsightsReport()
  },

  updated() {
    this.$nextTick(() => {
      if (!this.hasBeenDownloaded) {
        this.downloadingText = 'preparing_download'
        this.hasBeenDownloaded = true

        setTimeout(() => {
          this.generatePDF()
        }, 1000)
      }
    })
  },

  computed: {
    sections() {
      const sections = this.reportData?.sections ?? []
      return sections.map(section => {
        return this.getSanitizedSection(section)
      })
    },

    sanitizedTitle() {
      const title = this.reportData?.name ?? ''
      return this.$sanitize(title)
    },

    dateRange() {
      const startDate = this.routeQuery.startDate.replace(/-/g, '/')
      const endDate = this.routeQuery.endDate.replace(/-/g, '/')
      return `${startDate} - ${endDate}`
    },

    sanitizedFilters() {
      const query = this.routeQuery
      const { countryCodes = [], sites = [], tagGroups = [] } = query
      const filters = { ...query }
      delete filters.type
      delete filters.startDate
      delete filters.endDate
      return {
        ...filters,
        tagGroups: Array.isArray(tagGroups) ? tagGroups : [tagGroups],
        countryCodes: Array.isArray(countryCodes)
          ? countryCodes
          : [countryCodes],
        sites: Array.isArray(sites) ? sites : [sites],
      }
    },
  },

  methods: {
    ...mapActions({
      getReport: 'report/getReport',
    }),

    stopLoading() {
      this.isLoading = false
    },

    generatePDF() {
      html2canvas(this.$refs.reportPrint, {
        letterRendering: 1,
        allowTaint: true,
      }).then(canvas => {
        const imgData = canvas.toDataURL('image/png')
        const imgWidth = 214
        const pageHeight = 280
        const imgHeight = (canvas.height * imgWidth) / canvas.width
        let heightLeft = imgHeight
        const doc = createLetterPdf('p', jsPDF)
        let position = 10 // give some top padding to first page

        doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
        heightLeft -= pageHeight

        while (heightLeft >= 0) {
          position += heightLeft - imgHeight
          doc.addPage()
          doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
          heightLeft -= pageHeight
        }
        doc.save(`${this.routeQuery.type}-report ${PRIVACY_LABEL}.pdf`)
        this.stopLoading()
      })
    },

    async getInsightsReport() {
      try {
        this.reportData = await this.getReport({
          organizationId: this.routeQuery.organization,
          options: {
            type: this.routeQuery.type,
            startDate: utils.dates.getStartOfDay(this.routeQuery.startDate),
            endDate: utils.dates.getEndOfDay(this.routeQuery.endDate),
            filters: this.sanitizedFilters,
          },
        })
      } catch (err) {
        this.handleRequestError(err)
        this.stopLoading()
      }
    },

    getSanitizedSection(section) {
      const nonActionableWidgetd = section.widgets.filter(widget => {
        return !this.notAllowedWidgetsTypes.includes(widget.type)
      })

      section.widgets = nonActionableWidgetd
      return section
    },
  },
}
</script>

<style lang="scss" scoped>
@media screen {
  .report-print {
    margin: 64px;
    background-color: $color-white !important;
    box-shadow: unset !important;

    &-header,
    &-section {
      box-shadow: unset !important;
    }
  }
}

@media print {
  .report-print {
    margin: 10px !important;
    background-color: $color-white !important;
    box-shadow: unset !important;
  }
}
</style>
